/**
 * Device connection prompt.
 * Shows only on first visit if no device (Garmin, Apple Health, Strava) is connected.
 * Once dismissed, never shows again.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { GlassCard } from '@/components/ui/layout/card';
import { CardContent } from '@/components/ui/layout/card';
import { Watch, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  trackActivationDevicePromptShown,
  trackActivationDevicePromptClicked,
  trackActivationDevicePromptDismissed,
} from '@/lib/posthog';

const STORAGE_KEY = 'device_prompt_dismissed';

interface DeviceConnectionPromptProps {
  /** Whether any device is already connected */
  hasDevice: boolean;
}

export function DeviceConnectionPrompt({ hasDevice }: DeviceConnectionPromptProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const alreadyDismissed = localStorage.getItem(STORAGE_KEY) === '1';
  const [dismissed, setDismissed] = useState(alreadyDismissed);

  // Don't show if device connected or already dismissed
  if (hasDevice || dismissed) return null;

  trackActivationDevicePromptShown(0);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setDismissed(true);
    trackActivationDevicePromptDismissed(1);
  };

  const handleConnect = () => {
    trackActivationDevicePromptClicked('settings');
    navigate('/settings');
  };

  return (
    <GlassCard className="border-[#00FFC8]/20">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#00FFC8]/10 flex items-center justify-center shrink-0">
            <Watch className="h-5 w-5 text-[#00FFC8]" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-mono text-xs uppercase tracking-wider text-[#00FFC8]/90 mb-1">
              {t('dashboard.devicePrompt.title', { defaultValue: 'Connect your watch' })}
            </h4>
            <p className="text-xs text-muted-foreground leading-relaxed mb-3">
              {t('dashboard.devicePrompt.description', {
                defaultValue: 'Auto-sync workouts and get smarter, data-driven coaching.',
              })}
            </p>
            <Button
              onClick={handleConnect}
              size="sm"
              className={cn(
                'font-mono text-[10px] uppercase tracking-wider',
                'bg-[#00FFC8]/10 border border-[#00FFC8]/30 text-[#00FFC8]',
                'hover:bg-[#00FFC8]/20'
              )}
            >
              Connect Device
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="shrink-0 h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </GlassCard>
  );
}
